import { Button, LockIcon } from 'internalLibrary';
import { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { Formik } from 'formik';
import { QuestionnaireFormQuestion, QuestionnaireFormValues } from 'types/ui';
import { QuestionnaireQuestion } from '../QuestionnaireQuestion';
import { useAnalytics } from 'use-analytics';
import { useTranslation } from 'react-i18next';
import { WidnowWithFakeData } from 'utils/fakeDataConfig';
import layout from '../../../../../scss/layout.module.scss';
import styles from './QuestionnaireForm.module.scss';
import useInitialValues from './useInitialValues';
import useValidationSchema from './useValidationSchema';

interface QuestionnaireFormProps {
  questions: QuestionnaireFormQuestion[];
  onSubmit: (values: QuestionnaireFormValues) => Promise<void>;
  isSubmitting: boolean;
}

const QuestionnaireForm: FC<QuestionnaireFormProps> = (props) => {
  const { onSubmit, isSubmitting, questions } = props;
  const { t } = useTranslation();
  const validationSchema = useValidationSchema(questions);
  const initialValues = useInitialValues(questions);
  const fakeInitialValues = useInitialValues(questions, true);
  const { track } = useAnalytics();

  useEffect(() => {
    if (questions !== undefined) {
      (window as WidnowWithFakeData).fakeData = fakeInitialValues;
    }
  }, [questions]);

  const onClick = useCallback(() => {
    track('click', {
      description: 'submit',
    });
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        errors,
        touched,
      }) => {
        const onChange = (
          e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
          question: QuestionnaireFormQuestion,
        ) => {
          const answerId = parseInt(e.target.value.split('_').reverse()[0], 10);

          track('question', {
            ...question,
            value: question.answers.find((a) => a.id === answerId)
              ?.answerReference,
          });
          handleChange(e);
        };

        return (
          <form onSubmit={handleSubmit}>
            <div className={layout['width-lg']}>
              {questions.map((question) => {
                return (
                  <QuestionnaireQuestion
                    key={question.id}
                    question={question}
                    onChange={(e) => onChange(e, question)}
                    onBlur={handleBlur}
                    value={values[question.questionReference]}
                    touched={Boolean(touched[question.questionReference])}
                    error={errors[question.questionReference]}
                  />
                );
              })}

              <div>
                {Object.keys(errors).length > 1 && (
                  <p className={styles['from-error']}>
                    {t('correctFormErrorsAbove')}
                  </p>
                )}
                <Button
                  leftIcon={<LockIcon />}
                  type="submit"
                  fill
                  className={layout['width-md']}
                  loading={isSubmitting}
                  dataTest="button_submit"
                  onClick={onClick}
                >
                  {t('questionnaire_submitButtonText')}
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default QuestionnaireForm;
